/**
 * Branding, applied at runtime.
 *
 * Nothing about the product's identity is hardcoded in the interface. The owner
 * changes it in settings and the UI follows, with no rebuild.
 *
 * The important part is HOW the owner's colour is applied. It is not sprayed
 * onto components as inline styles; it is turned into an eight-step accent ramp
 * and written to CSS custom properties once, on `:root`. Every component then
 * consumes `accent-*` tokens and inherits the change for free — and because
 * `buildAccentRamp` pins the tonal structure and enforces WCAG AA on the steps
 * used as text, no brand colour can make the interface unreadable.
 *
 * The fixed tokens (background, foreground, borders, status colours) are never
 * touched. Those are the product's identity, not the owner's.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import {
  ACCENT_STEPS,
  DEFAULT_ACCENT_RAMP,
  buildAccentRamp,
  readableForeground,
  toRgbChannels,
  type PublicBranding,
  type UiLanguage,
} from "@laundrypro/shared";
import { apiFetch } from "../lib/api";

/** Matches the seed values written at first-run setup. */
const FALLBACK: PublicBranding = {
  appNameAr: "لاندري برو",
  appNameEn: "LaundryPro",
  primaryColor: DEFAULT_ACCENT_RAMP[500],
  secondaryColor: "#1C1917",
  logoUrl: null,
  faviconUrl: null,
  loginHeadlineAr: null,
  loginHeadlineEn: null,
  language: "ar",
  setupRequired: false,
};

interface BrandingState {
  readonly branding: PublicBranding;
  readonly language: UiLanguage;
  readonly appName: string;
  readonly loading: boolean;
  readonly refresh: () => Promise<void>;
}

const BrandingContext = createContext<BrandingState | null>(null);

export function BrandingProvider({ children }: { children: ReactNode }) {
  const [branding, setBranding] = useState<PublicBranding>(FALLBACK);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      setBranding(await apiFetch<PublicBranding>("/branding"));
    } catch {
      // A branding failure must never block sign-in. Defaults render a usable,
      // unbranded application rather than an error page.
      setBranding(FALLBACK);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  /*
   * Language, direction, typeface and document title are driven by ONE effect.
   * Splitting them is how a product ends up in Arabic with a Latin font, or
   * left-to-right with Arabic text — three sources of truth that drift.
   */
  useEffect(() => {
    const root = document.documentElement;

    root.lang = branding.language;
    root.dir = branding.language === "ar" ? "rtl" : "ltr";
    document.title = branding.language === "ar" ? branding.appNameAr : branding.appNameEn;

    const ramp = buildAccentRamp(branding.primaryColor);
    for (const step of ACCENT_STEPS) {
      root.style.setProperty(`--accent-${step}`, toRgbChannels(ramp[step]));
    }
    // Measured, not assumed: a light brand colour needs dark text on it.
    root.style.setProperty("--accent-foreground", toRgbChannels(readableForeground(ramp[500])));

    // The brand surface behind the login lockup — the one place the owner paints
    // a large area rather than an accent.
    root.style.setProperty("--brand-surface", toRgbChannels(branding.secondaryColor));

    if (branding.faviconUrl) {
      let link = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
      if (!link) {
        link = document.createElement("link");
        link.rel = "icon";
        document.head.appendChild(link);
      }
      link.href = branding.faviconUrl;
    }
  }, [branding]);

  const appName = branding.language === "ar" ? branding.appNameAr : branding.appNameEn;

  const value = useMemo<BrandingState>(
    () => ({ branding, language: branding.language, appName, loading, refresh }),
    [branding, appName, loading, refresh],
  );

  return <BrandingContext.Provider value={value}>{children}</BrandingContext.Provider>;
}

export function useBranding(): BrandingState {
  const context = useContext(BrandingContext);
  if (!context) throw new Error("useBranding must be used inside a BrandingProvider");
  return context;
}
